import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ExportNotification {
  id: string;
  jobOid: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  errorDetails?: string;
  primaryAction?: {
    label: string;
    handler: () => Promise<void>;
  };
  createdAt: Date;
  dismissedAt?: Date;
  actionTakenAt?: Date;
  sticky: boolean;
  position: 'top-right' | 'bottom-right';
}

@Injectable({
  providedIn: 'root',
})
export class ExportNotificationService {
  private readonly notificationsSubject = new BehaviorSubject<ExportNotification[]>([]);
  readonly notifications$: Observable<ExportNotification[]> = this.notificationsSubject.asObservable();
  private nextId = 0;

  add(notification: Omit<ExportNotification, 'id' | 'createdAt'>): void {
    const newNotification: ExportNotification = {
      ...notification,
      id: this.generateId(),
      createdAt: new Date(),
    };

    const current = this.notificationsSubject.value;
    this.notificationsSubject.next([...current, newNotification]);
  }

  dismiss(id: string): void {
    const current = this.notificationsSubject.value;
    const updated = current.filter(n => n.id !== id);
    this.notificationsSubject.next(updated);
  }

  executeAction(id: string): void {
    const current = this.notificationsSubject.value;
    const notification = current.find(n => n.id === id);

    if (notification?.primaryAction) {
      notification.primaryAction
        .handler()
        .then(() => {
          notification.actionTakenAt = new Date();
          this.dismiss(id);
        })
        .catch(() => {
          // Action failed, dismiss notification without logging
        });
    }
  }

  clear(): void {
    this.notificationsSubject.next([]);
  }

  private generateId(): string {
    return `notif-${Date.now()}-${this.nextId++}`;
  }
}
